import Delayable from '../../Core/mixin/Delayable.js';
import TaskEditStm from '../../Scheduler/feature/mixin/TaskEditStm.js';
import InstancePlugin from '../../Core/mixin/InstancePlugin.js';
import MessageDialog from '../../Core/widget/MessageDialog.js';
import Widget from '../../Core/widget/Widget.js';
import '../widget/SchedulerTaskEditor.js';
import ObjectHelper from '../../Core/helper/ObjectHelper.js';
import Rectangle from '../../Core/helper/util/Rectangle.js';
import TaskEditTransactional from '../../Scheduler/feature/mixin/TaskEditTransactional.js';
import TransactionalFeature from '../../Scheduler/feature/mixin/TransactionalFeature.js';
import StringHelper from '../../Core/helper/StringHelper.js';

/**
 * @module SchedulerPro/feature/TaskEditBase
 */

/**
 * Base class for {@link SchedulerPro/feature/TaskEdit taskEdit} and the Gantt `projectEdit` features.
 * @extends Core/mixin/InstancePlugin
 * @mixes Scheduler/feature/mixin/TaskEditStm
 * @mixes Core/mixin/Delayable
 * @abstract
 */
export default class TaskEditBase extends InstancePlugin.mixin(
    Delayable,
    TaskEditStm,
    TransactionalFeature,
    TaskEditTransactional
) {

    //region Config

    static $name = 'TaskEditBase';

    static configurable = {
        /**
         * The event that shall trigger showing the editor. Set to `` or null to disable editing.
         * @config {String}
         * @default
         * @category Editor
         */
        triggerEvent : '',

        entityName : '',

        // <remove-on-release>
        // TODO: deprecate it in favor of `editorConfig : { type : 'xxx' }`
        // </remove-on-release>
        /**
         * Class to use as the editor.
         * @config {Core.widget.Widget}
         * @typings {typeof Widget}
         * @category Editor
         */
        editorClass : null,

        disableEditorForReadOnlyRecord : true,

        /**
         * True to save and close this panel if ENTER is pressed in one of the input fields inside the panel.
         * @config {Boolean}
         * @default
         * @category Editor
         */
        saveAndCloseOnEnter : true,

        /**
         * What action should be taken when you click outside the editor, `cancel` or `save`
         * @config {'cancel'|'save'}
         * @default
         */
        blurAction : 'cancel',

        /**
         * The week start day used in all date fields of the feature editor form by default.
         * 0 means Sunday, 6 means Saturday.
         * Defaults to the locale's week start day.
         * @config {Number}
         */
        weekStartDay : null,

        items : null,

        /**
         * A configuration object applied to the internal editor.
         * @config {TaskEditorBaseConfig}
         */
        editorConfig : null,

        /**
         * When field in task editor is changed, project model normally will trigger `hasChanges` event. If you use
         * this event to handle project changes excessive events might be a problem. Set this flag to true to only
         * trigger single `hasChanges` event after task changes are applied.
         * @config {Boolean} suspendHasChangesEvent
         * @default
         */
        suspendHasChangesEvent : false
    };

    //endregion

    //region Constructor/Destructor

    construct(client, config) {
        const featureId = StringHelper.uncapitalize(this.constructor.$$name);

        client[featureId] = this;

        super.construct(client, ObjectHelper.assign({
            weekStartDay : client.weekStartDay
        }, config));

        client.ion({
            [this.triggerEvent] : 'onActivateEditor',
            readOnly            : 'onClientReadOnlyToggle',
            thisObj             : this
        });
    }

    doDestroy() {
        this.cleanupProjectListener();

        this.client.ownerForSchedulingIssueResolutionPopup = null;

        this.editor?.destroy();

        if (this.deleteConfirmationPromise) {
            MessageDialog.hide();
        }

        super.doDestroy();
    }

    //endregion

    //region Internal

    onClientReadOnlyToggle({ readOnly }) {
        if (this.editor) {
            this.editor.readOnly = readOnly;
        }
    }

    get scheduler() {
        return this.client;
    }

    get isValid() {
        return this.editor.eachWidget(widget => {
            if (widget.isValid === true || widget.hidden || widget.disabled || (widget.isField && !widget.name)) {
                return true;
            }

            return widget.isValid !== false;
        }, true);
    }

    //endregion

    //region Project

    get project() {
        return this.client.project;
    }

    setupProjectListener() {
        this.cleanupProjectListener();

        this.project.ion({
            name      : 'project',
            loadstart : () => this.save()
        });
    }

    cleanupProjectListener() {
        this.detachListeners('project');
    }

    //endregion

    //region Editor

    /**
     * Returns true if the editor is currently active
     * @readonly
     * @property {Boolean}
     */
    get isEditing() {
        return !!this._editing;
    }

    onActivateEditor({ record }) {
        // attempt to re-open already opened editor for the same record - do nothing
        if (record === this.record && this.isEditing) {
            return;
        }

        this.editRecord(record);
    }

    getRecordTargetElement(record) {}

    async onEditRecordPrevented(clearTransaction = false) {
        const
            me          = this,
            { project } = me;

        project.resumeAutoSync();

        // there's definitely a de-synchronization in the taskedit + stm + "transactional features" combination
        // however figuring out a proper solution is not easy, so applying a patch for now:
        // if we are in the middle of canceling - do not try to free the stm - that will be done as part of the
        // canceling action
        // what to do with the other calls like `resumeAutoSync/resumeChangeTracking` still to be determined
        if (!me._cancelling || clearTransaction) {
            // its not clear why `freeStm` method is called with `true` argument (meaning "commit transaction") here
            // it is covered with the `Should handle task being removed in dataset update happening in in beforeTaskEdit listener`
            // test in `SchedulerPro/lib/SchedulerPro/feature/TaskEdit.js` and the use case seems to be -
            // updating the dataset during the `beforeTaskEdit` event triggering, in one way this makes sense
            // however, not clear, why the task edit feature is responsible for committing the change -
            // shouldn't the "queue" mechanism prevent the dataset editing or some other synchronization occur?
            await me.freeStm();
        }

        if (me.isDestroyed) {
            return;
        }

        if (me.suspendHasChangesEvent && !project.isDestroying) {
            project.resumeChangeTracking();
        }

        me._editing = false;
    }

    normalizeRecord(record) {
        if (typeof record === 'function') {
            record = record();
        }

        // Open original record when editing a linked record
        record = record.$original;

        return record;
    }

    shouldEditRecord(record) {
        const
            { client }  = this,
            { project } = client;

        return !this.disabled && !project.isDelayingCalculation && (record.project || !client.usesDisplayStore);
    }

    getEditorShowByConfig(editor, targetElement) {
        const { client } = this;

        if (!editor.centered) {
            if (
                !client.timeAxisSubGrid.collapsed &&
                targetElement &&
                // In Gantt, ensure task bar is not out of view (if triggering edit from a grid cell)
                (!client.isGanttBase || Rectangle.from(targetElement).intersect(Rectangle.from(client.timeAxisSubGridElement)))
            ) {
                return {
                    target         : targetElement,
                    anchor         : true,
                    allowTargetOut : true,
                    offset         : -5
                };
            }
            else {
                // Display the editor centered in the Scheduler
                return {
                    target : client.element,
                    anchor : false,
                    // For records not part of the store (new ones, or filtered out ones) - center the editor
                    align  : 'c-c'
                };
            }
        }
    }

    async doEditRecord(record, targetElement = null, stmCapture = null) {
        const
            me          = this,
            { client }  = me,
            { project } = client;

        me._editing = true;

        if (stmCapture) {
            me.applyStmCapture(stmCapture);
            me.hasStmCapture = true;
            me.stmCapture = stmCapture;
        }
        else if (!me.hasStmCapture) {
            await me.captureStm(true);
        }

        project.suspendAutoSync();

        record = await me.normalizeRecord(record);

        if (!me.shouldEditRecord(record)) {
            await me.onEditRecordPrevented();
            return;
        }

        targetElement = targetElement || me.getRecordTargetElement(record);

        const editor = me.getEditor(record);

        if (await me.triggerOnClient('beforeRecordEdit', { record, targetElement }) !== false) {
            if (me.suspendHasChangesEvent) {
                client.project.suspendChangeTracking();
            }

            // Wait for any pending commit
            await project.commitAsync();

            if (me.isDestroyed) {
                return;
            }

            const startEditResult = await me.editRecordStart({
                // pass original arguments plus normalized ones
                callArguments : arguments,
                record,
                targetElement,
                stmCapture
            });

            if (me.isDestroyed) {
                return;
            }

            if (startEditResult === false) {
                await me.onEditRecordPrevented();
                return;
            }

            if (startEditResult) {
                record = startEditResult;
            }

            me.load(record);

            me.triggerOnClient('beforeRecordEditShow', {
                record,
                targetElement,
                editor
            });

            me.setupProjectListener();

            client.ownerForSchedulingIssueResolutionPopup = this.editor;

            const showByConfig = me.getEditorShowByConfig(editor, targetElement);

            if (showByConfig) {
                await editor.showBy(showByConfig);
            }
            else {
                await editor.show();
            }
        }
        else {
            me._cancelling = true;
            // Handle cleanup when edit is vetoed by `beforeRecordEdit` listener. The `onEditRecordPrevented` method has a condition
            // that prevents STM cleanup during normal cancellation (assuming cancel() method handles it). Since we're not using cancel()
            // here, we pass clearTransaction=true to ensure `onEditRecordPrevented` performs the necessary STM cleanup. We do
            // not reject the transaction here, as there can be some changes done in the listener, that we want to keep.
            await me.onEditRecordPrevented(true);
            me._cancelling = false;
        }
    }

    async editRecord(record, targetElement = null, stmCapture = null) {
        const
            me          = this,
            { client }  = me,
            doEdit      = me.shouldEditRecord(record, targetElement, stmCapture);

        if (doEdit && stmCapture) {
            // need to set this flag synchronously, to indicate to the outer world,
            // that we are going to take the ownership of the "stm capture"
            // only setting the flag, actual taking ownership happens later
            stmCapture.transferred = true;
        }

        // If we are editing, cancel the edit.
        if (me.isEditing) {
            await me.cancel();

            if (me.isDestroyed) {
                return;
            }
        }

        if (client.features.cellEdit?.isEditing) {
            client.cancelEditing();
        }

        // We may have just canceled the edit above, or some other user gesture may
        // have begun cancelling prior to that.
        // Either way, we must wait for it to cancel before the new edit can begin.
        await Promise.all([me._cancelling, me._hiding]).catch(() => { /* Do nothing on rejected Promise */ });

        if (me.isDestroyed) {
            return;
        }

        // Record without project might be a transient record in a display store, not meant to be manipulated
        if (doEdit) {
            return me.doEditRecord(record, targetElement, stmCapture);
        }
    }

    prepareEditorConfig(config) {
        return config;
    }

    getEditorTypeByRecord(record) {}

    getEditor(record = this.record, cfg = {}) {
        const
            me         = this,
            { client } = me;

        let { editor } = me;

        if (!editor) {
            const config = me.prepareEditorConfig(ObjectHelper.merge({
                editFeature         : me,
                weekStartDay        : me.weekStartDay,
                saveAndCloseOnEnter : me.saveAndCloseOnEnter,
                blurAction          : me.blurAction,
                owner               : client,
                project             : me.project,
                tabPanelItems       : me.items,
                internalListeners   : {
                    beforeHide : {
                        fn   : 'onBeforeHide',
                        // Unreasonable to prevent hide in non-prioritized listener
                        prio : -1000
                    },
                    cancel  : 'onCancel',
                    save    : 'onSave',
                    thisObj : me
                },
                // For backward compatibility
                tabsConfig : me.tabsConfig
            }, me.editorConfig, cfg));

            // Configured type should always win
            if (me.editorClass && !config.type) {
                editor = me.editor = me.editorClass.new(config);
            }
            else {
                editor = me.editor = Widget.create(ObjectHelper.assign({
                    type : me.getEditorTypeByRecord(record) || 'schedulertaskeditor'
                }, config));
            }
        }

        // Must set *after* construction, otherwise it becomes the default state to reset readOnly back to.
        editor.readOnly = client.readOnly || (me.disableEditorForReadOnlyRecord && record?.readOnly);

        editor.project = me.project;

        return editor;
    }

    //endregion

    //region Actions

    editRecordStart() {}

    afterSave() {}

    afterCancel() {
        super.afterCancel();
    }

    afterDelete() {}

    load(record, highlightChanges) {
        const
            me     = this,
            editor = me.getEditor(record);

        me._loading = true;

        me.record = record;
        editor.loadEvent(record, highlightChanges);

        me._loading = false;
    }

    async doSave() {
        // Turn a newly created record into a permanent one (no-op for others)
        this.record.isCreating = false;

        // Since we apply changes from fields right when user makes them
        // to save changes we just commit current STM transaction
        return this.freeStm(true);
    }

    /**
     * Call this method to close task editor saving changes.
     * @returns {Promise} A promise which is resolved when the task editor is closed and changes are saved.
     */
    async save() {
        const
            me = this,
            { client, record } = me;

        if (me.isEditing) {
            const editor = me.getEditor();

            if (!me.isValid || await me.triggerOnClient('beforeRecordSave', { record, editor }) === false) {
                return;
            }

            me.cleanupProjectListener();

            editor.beforeSave();

            // Editor close promise have a chance to get resolved after the transaction promise (e.g. if there's a
            // beforeClose listener on the widget). Therefore, we use a special flag to tell if promise is resolved to
            // make sure we don't skip `hasChanges` event.
            let skipChangeCheck = true;

            const commitPromise = me.doSave();

            if (ObjectHelper.isPromise(commitPromise)) {
                commitPromise.then(() => skipChangeCheck = false);
            }

            me._editing = false;
            client.ownerForSchedulingIssueResolutionPopup = null;

            // afterSave to happen only after the editor is fully invisible.
            await editor.close();

            if (ObjectHelper.isPromise(commitPromise)) {
                await commitPromise;

                if (me.isDestroying) {
                    return;
                }
            }

            client.project.resumeAutoSync(true);

            me.afterSave();

            me.triggerOnClient('afterRecordSave', { record, editor });

            editor.afterSave();

            if (me.suspendHasChangesEvent) {
                // Suspend event if transactional features are enabled. There would be another commitAsync on the project
                // which will trigger `hasChanges` event
                client.project.resumeChangeTracking(skipChangeCheck);
            }
            me.triggerOnClient('afterRecordEdit', { record, editor });
        }
    }

    // This is called by the TaskEditor's hide method prior to the super call,
    // so however it gets hidden, it will signal a cancel.
    async doCancel() {
        const
            me = this,
            { record, project } = me;

        if (me.isEditing) {
            // prevent the re-entrance to this method
            me._editing = false;

            me.cleanupProjectListener();

            me.client.ownerForSchedulingIssueResolutionPopup = null;

            const editor = me.getEditor();

            editor.editingRecurring = null;

            editor.beforeCancel();

            // the feature could get destroyed asynchronously
            if (me.isDestroyed) {
                // project could get destroyed at the same time, if configured with
                project.resumeAutoSync?.(false);
                return;
            }

            // We always need to reject transaction, to make sure we unblock the queue
            await me.rejectStmTransaction();

            if (record?.isCreating) {
                record.remove();
            }

            // the feature could get destroyed asynchronously
            if (me.isDestroyed) {
                // project could get destroyed at the same time, if configured with
                project.resumeAutoSync?.(false);
                return;
            }

            await project.commitAsync();

            // Need to close the editor so popup can be closed when 'taskEdit.cancel' is called
            await editor.hide?.();

            // the feature could get destroyed asynchronously
            if (me.isDestroyed) {
                // project could get destroyed at the same time, if configured with
                project.resumeAutoSync?.(false);
                return;
            }

            me.freeStm();

            me.afterCancel();

            editor.afterCancel();

            project.resumeAutoSync(false);

            me.triggerOnClient('recordEditCanceled', { record, editor });

            if (me.suspendHasChangesEvent) {
                project.resumeChangeTracking();
            }

            me.triggerOnClient(`afterRecordEdit`, { record, editor });
        }
    }

    /**
     * Call this method to close task editor reverting changes.
     * @returns {Promise} A promise which is resolved when the task editor is closed and changes are reverted.
     */
    async cancel() {
        const me = this;

        return me._cancelling || (me._cancelling = me.doCancel().finally(() => me._cancelling = undefined));
    }

    async delete() {
        const
            me = this,
            { editor, client, record, project } = me;

        if (!record.isCreating && await me.triggerOnClient(`beforeRecordDelete`, { record, editor }) === false) {
            return;
        }

        me.cleanupProjectListener();

        client.ownerForSchedulingIssueResolutionPopup = null;

        editor.beforeDelete();

        record.remove();

        await me.commitStmTransaction();

        // the feature could get destroyed asynchronously
        if (me.isDestroyed) {
            // project could get destroyed at the same time, if configured with
            project.resumeAutoSync?.(false);
            return;
        }

        me.freeStm();

        await project.commitAsync();

        // the feature could get destroyed asynchronously
        if (me.isDestroyed) {
            return;
        }

        // Resume and sync, unless we are removing a newly created record (via cancel)
        project.resumeAutoSync(!record.isCreating);

        me._editing = false;

        editor.close();

        editor.afterDelete();

        me.afterDelete();

        if (me.suspendHasChangesEvent) {
            client.project.resumeChangeTracking();
        }

        me.triggerOnClient(`afterRecordEdit`, { editor, record });
    }

    //endregion

    //region Events

    onSave() {
        // There's might be propagation requested, so we giving the chance to start propagating
        // before we're doing save commit procedure.
        this.requestAnimationFrame(() => this.save());
    }

    onCancel() {
        this.cancel().then();
    }

    onBeforeHide({ source, animate }) {
        // It should not matter that we might override previous promise. Any chained promises still are going to be
        // executed. We only need to make sure that current promise and doCancel promises end at the same time.
        if (source.hideAnimation && animate !== false) {
            this._hiding = new Promise(resolve => {
                source.ion({
                    hideAnimationEnd : resolve,
                    expires          : {
                        delay : 500,
                        alt   : resolve
                    }

                });
            }).finally(() => this._hiding = undefined);
        }
    }

    //endregion

    triggerOnClient(name, params, entityName = this.entityName) {
        const entityNameCap = StringHelper.capitalize(entityName);

        if (entityName) {
            name = name.replace(/record/, entityName).replace(/Record/, entityNameCap);
            params[entityName + 'Record'] = params.record;
            params[entityName + 'Element'] = params.targetElement;
        }

        return this.client.trigger(name, params);
    }

}
